// The words the app puts in a request when it hands one over: to Claude in
// the chat beside the board, or to the user's own agent (Claude Code and the
// rest), which needs to be told it is the Context Board. One place, so the
// gallery, the Look panel, the Inspector and the command bar all ask the same
// way. No tool names: the user sees these words, and the agents find the
// right tools from the board's own instructions.

import type { WidgetItem } from "../../shared/types";
import type { WidgetExample } from "../../shared/widgetExamples";
import type { Ask } from "./handoff";

const titled = (w: WidgetItem) => `“${w.title || "Custom"}”`;

/** Build a widget, perhaps like one of the finished examples. */
export function widgetAsk(text: string, like: WidgetExample | null = null): Ask {
  const t = text.trim();
  const what = like ? `like the “${like.name}” example${t ? `: ${t}` : ", with my own numbers. Ask me where they come from."}` : `: ${t}`;
  return { chat: `Make a widget ${what}`.replace("widget :", "widget:"), agent: `On my Context Board, make a widget ${what}`.replace("widget :", "widget:") };
}

/** Make a theme from a description. */
export function themeAsk(idea: string): Ask {
  const t = idea.trim();
  return { chat: `Make a theme: ${t}`, agent: `On my Context Board, make a theme: ${t}${/[.!?…]["”’)]*$/.test(t) ? "" : "."}` };
}

/** Change a widget: the user says what. */
export function changeAsk(w: WidgetItem): Ask {
  return { chat: `Change the custom widget ${titled(w)}: `, agent: `On my Context Board, change the custom widget ${titled(w)} (id ${w.id}): `, finish: true };
}

/** Change what a button does, or its words (Round 7): the user says what, and OKs the new task on its next press. */
export function buttonAsk(w: WidgetItem): Ask {
  const name = `“${w.title || "Button"}”`;
  return { chat: `Change the button ${name}: `, agent: `On my Context Board, change the button ${name} (id ${w.id}): `, finish: true };
}

/** Fix a widget, saying what went wrong. */
export function fixAsk(w: WidgetItem, what: string): Ask {
  return { chat: `Fix the custom widget ${titled(w)}.${what ? " " + what : ""}`, agent: `On my Context Board, fix the custom widget ${titled(w)} (id ${w.id}).${what ? " " + what : ""}` };
}

/** Draw a custom widget that has no code yet. */
export function drawAsk(w: WidgetItem): Ask {
  return { chat: `Draw the custom widget ${titled(w)}: `, agent: `On my Context Board, draw the custom widget ${titled(w)} (id ${w.id}): `, finish: true };
}

/** Write a brief widget's words, from what is on the board, into the stream it reads. */
export function briefAsk(w: WidgetItem, stream: string): Ask {
  const shape = "Lead with the one thing that matters most today, in a line. Then what changed since the last brief, what needs me, and the three things to do first. Short lines, and only numbers that are on the board.";
  return {
    chat: `Write today's brief in ${titled(w)}, from what is on the board. ${shape}`,
    agent: `On my Context Board, write today's brief in the widget ${titled(w)} (id ${w.id}), from what is on the board: read the board, then send the brief as text to the stream "${stream}". ${shape}`,
  };
}
